import { Check, AlertTriangle, ShieldQuestion } from 'lucide-react'
import type { FieldVerificationItem } from '../types'

export type BadgeState = 'unverified' | 'verified' | 'requires_attention'

export type FieldVerificationBadgeProps = {
  state: BadgeState
  canVerify: boolean
  verification?: FieldVerificationItem | null
  verifierRoleLabels?: string[]
  onVerifyClick: () => void
  onViewDetailsClick: () => void
}

export function FieldVerificationBadge({
  state,
  canVerify,
  verification,
  verifierRoleLabels,
  onVerifyClick,
  onViewDetailsClick,
}: FieldVerificationBadgeProps) {
  const disabled = !canVerify && state !== 'verified'

  const handleClick = () => {
    if (disabled) return
    if (state === 'verified') onViewDetailsClick()
    else onVerifyClick()
  }

  const base = 'inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors'
  const cls = state === 'verified'
    ? `${base} text-emerald-600 hover:bg-emerald-50`
    : state === 'requires_attention'
    ? `${base} text-red-600 hover:bg-red-50`
    : `${base} text-slate-400 ${canVerify ? 'hover:bg-slate-100' : 'cursor-not-allowed opacity-60'}`

  const Icon = state === 'verified'
    ? Check
    : state === 'requires_attention'
    ? AlertTriangle
    : ShieldQuestion

  const title = buildTitle(state, canVerify, verification, verifierRoleLabels)

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={cls}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}

function buildTitle(
  state: BadgeState,
  canVerify: boolean,
  verification?: FieldVerificationItem | null,
  verifierRoleLabels?: string[],
): string {
  if (state === 'verified' && verification) {
    const who = verification.verifiedByName ?? `użytkownik #${verification.verifiedByUserId}`
    return `Zatwierdził: ${who} (${formatPl(verification.verifiedAt)})`
  }
  if (state === 'requires_attention') {
    return 'Wartość zmieniła się od ostatniej weryfikacji - wymaga ponownego zatwierdzenia'
  }
  if (canVerify) return 'Pole wymaga weryfikacji - kliknij, aby zatwierdzić'
  const roles = verifierRoleLabels?.length ? ` (${verifierRoleLabels.join(', ')})` : ''
  return `Pole oczekuje na weryfikację${roles}`
}

function formatPl(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })
}
